import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Truck, Clock, ArrowRight, Check, X, Box } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from '../hooks/use-toast';
import { shipmentAPI } from '../services/api';
import { serviceTypes, travelModes, deliveryTypes } from '../data/constants';
import { provinceList, getProvinceCities } from '../data/cities';

const HomePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [step, setStep] = useState(1);

  // Origin/Destination province selectors on hero
  const [originProvince, setOriginProvince] = useState('');
  const [destProvince, setDestProvince] = useState('');

  const [formData, setFormData] = useState({
    origin_province: '',
    origin_city: '',
    origin_details: '',
    dest_province: '',
    dest_city: '',
    dest_details: '',
    sender_name: '',
    sender_phone: '',
    sender_email: '',
    receiver_name: '',
    receiver_phone: '',
    receiver_email: '',
    item_description: '',
    travel_mode: '',
    delivery_type: '',
    box_type: '',
    length: '',
    width: '',
    height: '',
    weight: '',
    package_value: '',
  });

  const [selectedService, setSelectedService] = useState('');
  const [priceInfo, setPriceInfo] = useState(null);
  const [calculatingPrice, setCalculatingPrice] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleShipNow = () => {
    if (!originProvince || !destProvince) {
      toast({
        title: "Please select provinces",
        description: "Select both origin and destination provinces to continue.",
        variant: "destructive"
      });
      return;
    }

    // Require login
    if (!localStorage.getItem('auth_token')) {
      toast({
        title: "Login Required",
        description: "Please log in or create an account to book a shipment.",
        variant: "destructive"
      });
      navigate('/login');
      return;
    }

    setFormData(prev => ({
      ...prev,
      origin_province: originProvince,
      dest_province: destProvince,
      origin_city: '',
      dest_city: '',
    }));
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setStep(1);
    setSelectedService('');
    setPriceInfo(null);
  };

  const handleBoxSelect = async (boxType) => {
    let length, width, height, weight;
    if (boxType === 'box1') {
      length = 100; width = 100; height = 100; weight = 200;
    } else if (boxType === 'box2') {
      length = 200; width = 200; height = 200; weight = 400;
    }

    if (boxType !== 'custom') {
      setFormData(prev => ({
        ...prev,
        box_type: boxType,
        length: String(length),
        width: String(width),
        height: String(height),
        weight: String(weight),
      }));
      await fetchPrice(length, width, height, weight);
    } else {
      setFormData(prev => ({
        ...prev,
        box_type: 'custom',
        length: '',
        width: '',
        height: '',
        weight: '',
      }));
      setPriceInfo(null);
    }
  };

  const fetchPrice = async (l, w, h, wt) => {
    if (!formData.delivery_type || !formData.travel_mode) return;
    const pkgVal = parseFloat(formData.package_value) || 100;
    setCalculatingPrice(true);
    try {
      const data = await shipmentAPI.calculatePrice({
        length: parseFloat(l),
        width: parseFloat(w),
        height: parseFloat(h),
        weight: parseFloat(wt),
        package_value: Math.max(pkgVal, 100),
        delivery_type: formData.delivery_type,
        travel_mode: formData.travel_mode,
        origin_province: formData.origin_province,
        dest_province: formData.dest_province,
      });
      setPriceInfo(data);
    } catch {
      setPriceInfo(null);
    } finally {
      setCalculatingPrice(false);
    }
  };

  const handleCustomConfirm = async () => {
    const { length, width, height, weight } = formData;
    if (!length || !width || !height || !weight) {
      toast({ title: "Fill all dimensions", variant: "destructive" });
      return;
    }
    if (parseFloat(weight) < 1) {
      toast({ title: "Weight must be at least 1 kg", variant: "destructive" });
      return;
    }
    await fetchPrice(length, width, height, weight);
  };

  const handleFormComplete = (e) => {
    e.preventDefault();

    if (!formData.origin_city || !formData.origin_details ||
        !formData.dest_city || !formData.dest_details ||
        !formData.sender_name || !formData.sender_phone || !formData.sender_email ||
        !formData.receiver_name || !formData.receiver_phone || !formData.receiver_email ||
        !formData.item_description || !formData.package_value ||
        !formData.travel_mode || !formData.delivery_type ||
        !formData.box_type) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required fields.",
        variant: "destructive"
      });
      return;
    }

    if (!formData.length || !formData.width || !formData.height || !formData.weight) {
      toast({
        title: "Select a box or fill custom dimensions",
        variant: "destructive"
      });
      return;
    }

    if (!priceInfo) {
      toast({
        title: "Price not calculated",
        description: "Please select a box or confirm custom dimensions first.",
        variant: "destructive"
      });
      return;
    }

    setStep(2);
  };

  const handleSubmit = async () => {
    if (!selectedService) {
      toast({
        title: "Select Service Type",
        description: "Please select a service type to continue.",
        variant: "destructive"
      });
      return;
    }

    setLoading(true);
    try {
      const shipment = await shipmentAPI.createShipment({
        sender_name: formData.sender_name,
        sender_phone: formData.sender_phone,
        sender_email: formData.sender_email,
        sender_address: `${formData.origin_province}, ${formData.origin_city}, ${formData.origin_details}`,
        receiver_name: formData.receiver_name,
        receiver_phone: formData.receiver_phone,
        receiver_email: formData.receiver_email,
        receiver_address: `${formData.dest_province}, ${formData.dest_city}, ${formData.dest_details}`,
        item_description: formData.item_description,
        service_type: selectedService,
        travel_mode: formData.travel_mode,
        delivery_type: formData.delivery_type,
        box_type: formData.box_type,
        length: parseFloat(formData.length),
        width: parseFloat(formData.width),
        height: parseFloat(formData.height),
        weight: parseFloat(formData.weight),
        package_value: Math.max(parseFloat(formData.package_value) || 100, 100),
        price: priceInfo?.price || 0,
      });

      toast({
        title: "Booking Successful!",
        description: `Your tracking number is: ${shipment.tracking_number}`,
      });

      handleCloseModal();
      setTimeout(() => {
        navigate(`/track?number=${shipment.tracking_number}`);
      }, 2000);
    } catch (error) {
      toast({
        title: "Booking Failed",
        description: error.response?.data?.detail || "Failed to create shipment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#D62828] to-[#A01E1E] text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <h1 className="text-5xl font-bold" data-testid="hero-title">Just In Time "JIT" Cargo Express</h1>
          </div>
          <p className="text-xl text-gray-100 mb-2">Just In Time - Every Time</p>
          <p className="text-lg text-gray-200 mb-12">Fast, Reliable Cargo Delivery Across the Philippines</p>

          {/* Origin/Destination Province Widget */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-8">
              <div className="flex flex-col sm:flex-row items-center gap-4">
                <div className="flex-1 w-full">
                  <Select value={originProvince} onValueChange={setOriginProvince}>
                    <SelectTrigger data-testid="origin-province-select" className="h-16 text-lg border-2 border-gray-300 bg-white text-gray-900">
                      <SelectValue placeholder="Origin Province" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinceList.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="hidden sm:block">
                  <ArrowRight className="w-8 h-8 text-gray-400" />
                </div>
                <div className="flex-1 w-full">
                  <Select value={destProvince} onValueChange={setDestProvince}>
                    <SelectTrigger data-testid="dest-province-select" className="h-16 text-lg border-2 border-gray-300 bg-white text-gray-900">
                      <SelectValue placeholder="Destination Province" />
                    </SelectTrigger>
                    <SelectContent>
                      {provinceList.map((prov) => (
                        <SelectItem key={prov} value={prov}>{prov}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <Button
                  data-testid="ship-now-btn"
                  onClick={handleShipNow}
                  className="bg-[#FDD935] hover:bg-[#F5D020] text-black font-bold px-10 h-16 text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl w-full sm:w-auto"
                >
                  Ship now
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Features */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Clock className="w-12 h-12 text-[#D62828] mx-auto mb-3" />
            <h3 className="font-bold text-lg mb-2">Same Day Delivery</h3>
            <p className="text-gray-600 text-sm">Get your cargo delivered within hours</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Package className="w-12 h-12 text-[#D62828] mx-auto mb-3" />
            <h3 className="font-bold text-lg mb-2">Secure Handling</h3>
            <p className="text-gray-600 text-sm">100% insured shipments</p>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <Truck className="w-12 h-12 text-[#D62828] mx-auto mb-3" />
            <h3 className="font-bold text-lg mb-2">Real-Time Tracking</h3>
            <p className="text-gray-600 text-sm">Track your shipment anytime</p>
          </div>
        </div>
      </div>

      {/* Booking Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-8 py-4 flex items-center justify-between z-10">
              <h2 className="text-2xl font-bold text-gray-900" data-testid="modal-title">
                {step === 1 ? 'Complete Your Booking' : 'Choose Your Service'}
              </h2>
              <button onClick={handleCloseModal} className="p-2 hover:bg-gray-100 rounded-full transition-colors" data-testid="modal-close-btn">
                <X className="w-6 h-6 text-gray-600" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-8">
              {step === 1 ? (
                <form onSubmit={handleFormComplete} className="space-y-6">
                  {/* Origin */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Origin</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Province *</Label>
                        <Input value={formData.origin_province} disabled className="mt-1 bg-gray-100" />
                      </div>
                      <div>
                        <Label>City *</Label>
                        <Select value={formData.origin_city} onValueChange={(val) => handleChange('origin_city', val)}>
                          <SelectTrigger className="mt-1" data-testid="origin-city-select">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            {getProvinceCities(formData.origin_province).map((city) => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Barangay / Street *</Label>
                        <Textarea
                          data-testid="origin-details"
                          value={formData.origin_details}
                          onChange={(e) => handleChange('origin_details', e.target.value)}
                          placeholder="Complete address details"
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Destination */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Destination</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Province *</Label>
                        <Input value={formData.dest_province} disabled className="mt-1 bg-gray-100" />
                      </div>
                      <div>
                        <Label>City *</Label>
                        <Select value={formData.dest_city} onValueChange={(val) => handleChange('dest_city', val)}>
                          <SelectTrigger className="mt-1" data-testid="dest-city-select">
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            {getProvinceCities(formData.dest_province).map((city) => (
                              <SelectItem key={city} value={city}>{city}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="md:col-span-2">
                        <Label>Barangay / Street *</Label>
                        <Textarea
                          data-testid="dest-details"
                          value={formData.dest_details}
                          onChange={(e) => handleChange('dest_details', e.target.value)}
                          placeholder="Complete address details"
                          className="mt-1"
                          rows={2}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Sender Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Sender Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input data-testid="sender-name" value={formData.sender_name} onChange={(e) => handleChange('sender_name', e.target.value)} placeholder="Sender full name" className="mt-1" />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input data-testid="sender-phone" value={formData.sender_phone} onChange={(e) => handleChange('sender_phone', e.target.value)} placeholder="09XXXXXXXXX" className="mt-1" />
                      </div>
                      <div>
                        <Label>Email Address *</Label>
                        <Input data-testid="sender-email" type="email" value={formData.sender_email} onChange={(e) => handleChange('sender_email', e.target.value)} placeholder="email@example.com" className="mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* Receiver Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Receiver Details</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>Full Name *</Label>
                        <Input data-testid="receiver-name" value={formData.receiver_name} onChange={(e) => handleChange('receiver_name', e.target.value)} placeholder="Receiver full name" className="mt-1" />
                      </div>
                      <div>
                        <Label>Phone Number *</Label>
                        <Input data-testid="receiver-phone" value={formData.receiver_phone} onChange={(e) => handleChange('receiver_phone', e.target.value)} placeholder="09XXXXXXXXX" className="mt-1" />
                      </div>
                      <div>
                        <Label>Email Address *</Label>
                        <Input data-testid="receiver-email" type="email" value={formData.receiver_email} onChange={(e) => handleChange('receiver_email', e.target.value)} placeholder="email@example.com" className="mt-1" />
                      </div>
                    </div>
                  </div>

                  {/* Shipment Details */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Shipment Details</h3>
                    <div className="mb-4">
                      <Label>Describe Your Item *</Label>
                      <Textarea data-testid="item-description" value={formData.item_description} onChange={(e) => handleChange('item_description', e.target.value)} placeholder="e.g. Electronics, documents, fragile items..." className="mt-1" rows={2} />
                    </div>
                    <div className="mb-4">
                      <Label>Package Value (₱) * <span className="text-xs text-gray-500 font-normal">min ₱100</span></Label>
                      <Input
                        data-testid="package-value"
                        type="number"
                        min="100"
                        step="1"
                        value={formData.package_value}
                        onChange={(e) => { handleChange('package_value', e.target.value); setPriceInfo(null); }}
                        placeholder="Declared value of package (min ₱100)"
                        className="mt-1"
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>Travel Mode *</Label>
                        <Select value={formData.travel_mode} onValueChange={(value) => { handleChange('travel_mode', value); setPriceInfo(null); }}>
                          <SelectTrigger className="mt-1" data-testid="travel-mode-select">
                            <SelectValue placeholder="Select travel mode" />
                          </SelectTrigger>
                          <SelectContent>
                            {travelModes.map((mode) => (
                              <SelectItem key={mode} value={mode}>{mode}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Delivery Type *</Label>
                        <Select value={formData.delivery_type} onValueChange={(value) => { handleChange('delivery_type', value); setPriceInfo(null); }}>
                          <SelectTrigger className="mt-1" data-testid="delivery-type-select">
                            <SelectValue placeholder="Select delivery type" />
                          </SelectTrigger>
                          <SelectContent>
                            {deliveryTypes.map((type) => (
                              <SelectItem key={type} value={type}>{type}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Box Selection */}
                  {formData.travel_mode && formData.delivery_type && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Choose Box</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <button type="button" data-testid="box1-select" onClick={() => handleBoxSelect('box1')}
                          className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${formData.box_type === 'box1' ? 'border-[#D62828] bg-[#D62828]/5 shadow-lg' : 'border-gray-200 hover:border-[#D62828]/50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Box className="w-8 h-8 text-[#D62828]" />
                            {formData.box_type === 'box1' && <Check className="w-5 h-5 text-[#D62828]" />}
                          </div>
                          <h4 className="font-bold text-gray-900">Box 1</h4>
                          <p className="text-sm text-gray-600 mt-1">0.5 to 1 CBM</p>
                          <p className="text-sm text-gray-600">100x100x100 cm / 200 kg</p>
                        </button>

                        <button type="button" data-testid="box2-select" onClick={() => handleBoxSelect('box2')}
                          className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${formData.box_type === 'box2' ? 'border-[#D62828] bg-[#D62828]/5 shadow-lg' : 'border-gray-200 hover:border-[#D62828]/50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Box className="w-8 h-8 text-[#D62828]" />
                            {formData.box_type === 'box2' && <Check className="w-5 h-5 text-[#D62828]" />}
                          </div>
                          <h4 className="font-bold text-gray-900">Box 2</h4>
                          <p className="text-sm text-gray-600 mt-1">1.1 to 2 CBM</p>
                          <p className="text-sm text-gray-600">200x200x200 cm / 400 kg</p>
                        </button>

                        <button type="button" data-testid="custom-box-select" onClick={() => handleBoxSelect('custom')}
                          className={`p-5 rounded-xl border-2 transition-all duration-200 text-left ${formData.box_type === 'custom' ? 'border-[#D62828] bg-[#D62828]/5 shadow-lg' : 'border-gray-200 hover:border-[#D62828]/50'}`}>
                          <div className="flex items-center justify-between mb-2">
                            <Package className="w-8 h-8 text-[#D62828]" />
                            {formData.box_type === 'custom' && <Check className="w-5 h-5 text-[#D62828]" />}
                          </div>
                          <h4 className="font-bold text-gray-900">Custom Box</h4>
                          <p className="text-sm text-gray-600 mt-1">Enter your own dimensions</p>
                          <p className="text-sm text-gray-600">& weight</p>
                        </button>
                      </div>

                      {/* Custom Box Form */}
                      {formData.box_type === 'custom' && (
                        <div className="mt-4 p-5 border-2 border-dashed border-gray-300 rounded-xl bg-gray-50">
                          <h4 className="font-semibold text-gray-900 mb-3">Custom Box Dimensions</h4>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <Label>Length (cm) *</Label>
                              <Input data-testid="custom-length" type="number" step="0.1" value={formData.length} onChange={(e) => { handleChange('length', e.target.value); setPriceInfo(null); }} placeholder="L" className="mt-1" />
                            </div>
                            <div>
                              <Label>Width (cm) *</Label>
                              <Input data-testid="custom-width" type="number" step="0.1" value={formData.width} onChange={(e) => { handleChange('width', e.target.value); setPriceInfo(null); }} placeholder="W" className="mt-1" />
                            </div>
                            <div>
                              <Label>Height (cm) *</Label>
                              <Input data-testid="custom-height" type="number" step="0.1" value={formData.height} onChange={(e) => { handleChange('height', e.target.value); setPriceInfo(null); }} placeholder="H" className="mt-1" />
                            </div>
                            <div>
                              <Label>Weight (kg) *</Label>
                              <Input data-testid="custom-weight" type="number" step="0.1" min="1" value={formData.weight} onChange={(e) => { handleChange('weight', e.target.value); setPriceInfo(null); }} placeholder="min 1 kg" className="mt-1" />
                            </div>
                          </div>
                          <Button type="button" data-testid="confirm-custom-btn" onClick={handleCustomConfirm} disabled={calculatingPrice} className="mt-4 bg-[#D62828] hover:bg-[#B01F1F] text-white font-semibold">
                            {calculatingPrice ? 'Calculating...' : 'Confirm Dimensions'}
                          </Button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Price Display */}
                  {priceInfo && (
                    <div className="bg-gradient-to-r from-[#D62828]/10 to-[#D62828]/5 border border-[#D62828]/20 rounded-xl p-5" data-testid="price-display">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Estimated Shipping Price</p>
                        <p className="text-3xl font-bold text-[#D62828]" data-testid="price-value">
                          ₱{priceInfo.price?.toLocaleString()}
                        </p>
                      </div>
                      <p className="text-sm text-[#D62828] font-semibold mt-3 text-center" data-testid="discount-text">
                        For discounts Call n txt 09989996788
                      </p>
                    </div>
                  )}

                  <Button type="submit" data-testid="continue-service-btn" className="w-full bg-[#D62828] hover:bg-[#B01F1F] text-white font-bold py-4 text-base rounded-lg">
                    Continue to Service Selection
                  </Button>
                </form>
              ) : (
                <div className="space-y-6">
                  <p className="text-gray-600 text-center">Select the service that best fits your needs</p>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {serviceTypes.map((service) => (
                      <button key={service} onClick={() => setSelectedService(service)}
                        data-testid={`service-${service.replace(/\s+/g, '-').toLowerCase()}`}
                        className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${selectedService === service ? 'border-[#D62828] bg-[#D62828]/5 shadow-lg' : 'border-gray-200 hover:border-[#D62828]/50'}`}>
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-bold text-base text-gray-900">{service}</h3>
                          {selectedService === service && <Check className="w-5 h-5 text-[#D62828] flex-shrink-0" />}
                        </div>
                      </button>
                    ))}
                  </div>

                  {priceInfo && (
                    <div className="bg-gradient-to-r from-[#D62828]/10 to-[#D62828]/5 border border-[#D62828]/20 rounded-xl p-5">
                      <div className="flex items-center justify-between">
                        <p className="text-sm font-medium text-gray-700">Total Price</p>
                        <p className="text-2xl font-bold text-[#D62828]">₱{priceInfo.price.toLocaleString()}</p>
                      </div>
                    </div>
                  )}

                  <Button onClick={handleSubmit} disabled={loading || !selectedService} data-testid="complete-booking-btn" className="w-full bg-[#D62828] hover:bg-[#B01F1F] text-white font-bold py-4 text-base rounded-lg">
                    {loading ? 'Processing...' : 'Complete Booking'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HomePage;
